import { Entity } from '@backstage/catalog-model';
import { EmptyState, InfoCard } from '@backstage/core-components';
import { EntityRefLink, useEntity } from '@backstage/plugin-catalog-react';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import Edit from '@material-ui/icons/Edit';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import StarIcon from '@material-ui/icons/Star';
import useCustomEntities from '../../hooks/useEntities';
import { dhDockStore, RootState } from '../../redux/store';
import { productCatalogBaseUrl } from '../../utils';

const useStyles = makeStyles({
  link: {
    color: '#05164d',
    cursor: 'pointer',
    display: 'flex',
    marginTop: '10px',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  container: {
    position: 'relative',
  },
  button: {
    height: '34px',
    lineHeight: '10px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'darkblue',
    float: 'right',
    marginTop: '-70px',
    '& hover': {
      color: 'red',
    },
  },
});

interface contributingTeamsProp {
  vs: string;
  team: string;
  teamName?: string;
  vsName?: string;
}

interface contributingEntraIdProps {
  name: string;
  entraidObjectId: string;
}

const CoownersTeams = () => {
  const store = dhDockStore.getState();
  const classes = useStyles();
  const { entity } = useEntity();

  const [contributingTeamsList, setContributingTeamsList] = useState<
    contributingTeamsProp[]
  >([]);
  const { list } = store.entraIdGroups as any;
  const { response, error, loading } = useSelector(
    (state: RootState) => state.hierachyList,
  );
  const { getCustomEntitiesByKind: groupList } = useCustomEntities('Group');

  const handleContributingList = useCallback(async () => {
    const resolvedgroupList = await (await groupList()).items;
    if (
      entity?.spec?.contributingTeams &&
      Array.isArray(list) &&
      list.length > 0
    ) {
      const { contributingTeams } = entity.spec;

      if (Array.isArray(contributingTeams)) {
        const contributingTeamNames: contributingEntraIdProps[] = [];

        contributingTeams.forEach(itemUid => {
          const match = list.find(x => x.uid === itemUid);
          if (match?.name) {
            contributingTeamNames.push({
              name: match.name,
              entraidObjectId: match?.entraid_team_objectid,
            });
          }
        });
        if (
          contributingTeamNames.length > 0 &&
          !loading &&
          !error &&
          resolvedgroupList.length > 0
        ) {
          // loop through the names and match them with
          contributingTeamNames.forEach(data => {
            const match = resolvedgroupList?.find(
              x =>
                x.metadata.annotations?.[
                  'graph.microsoft.com/group-id'
                ]?.toLowerCase() === data.entraidObjectId,
            ) as Entity;
            if (match && Object.keys(match).length > 0) {
              let vs_: string = '';
              if (!match?.metadata?.name?.toLowerCase()?.includes('_vs_')) {
                const art = match?.relations
                  ?.find(
                    x =>
                      x.targetRef?.toLowerCase()?.includes('_art_') ||
                      x.targetRef?.toLowerCase()?.includes('_vs_') ||
                      x.targetRef?.toLowerCase()?.includes('_cop_') ||
                      (x.targetRef?.toLowerCase()?.includes('_plt_') &&
                        x.type === 'childOf'),
                  )
                  ?.targetRef?.toLowerCase()
                  ?.replace('group:default/', '') as string;
                const data = resolvedgroupList?.find(
                  x => x.metadata.name?.toLowerCase() === art,
                ) as Entity;

                const { profile } = match?.spec as any;

                const vs =
                  (data?.spec!.parent as string) ??
                  data?.relations?.find(x =>
                    x.targetRef?.toLowerCase()?.includes('_vs_'),
                  );

                if (!vs) {
                  const platform = data?.relations?.find(({ targetRef }) => {
                    const ref = targetRef?.toLowerCase();
                    return (
                      ref?.includes('_plt') ||
                      ref?.includes('_platform') ||
                      ref?.includes('_art') || ref?.includes('_cop_')
                    );
                  });
                  const res = resolvedgroupList?.find(
                    x =>
                      x.metadata?.name?.toLowerCase() ===
                      platform?.targetRef
                        ?.toLowerCase()
                        ?.replace('group:default/', ''),
                  );

                  vs_ =
                    (res?.relations?.find(x =>
                      x.targetRef?.toLowerCase()?.includes('_vs_'),
                    )?.targetRef as string) ??
                    (data?.spec?.parent as string) ??
                    'group:default/lufthansa_group';
                } else {
                  vs_ =
                    vs?.toLowerCase() ===
                    'group:default/dh_digitalhangarpeopleappgroups'
                      ? (data?.metadata?.name as string)
                      : (vs as string);
                }

                const team: contributingTeamsProp = {
                  team: match?.metadata?.name as string,
                  vs:
                    data?.relations?.find(x =>
                      x.targetRef?.toLowerCase()?.includes('_vs_'),
                    )?.targetRef ?? vs_,
                  teamName: profile?.displayName,
                };
                setContributingTeamsList(oldArr => [...oldArr, team]);
              }
            }
          });
        }
      }
    } else {
      const team = entity?.spec?.owner as string;
      if (team) {
        const match = response?.find(
          x => x.team?.toLowerCase() === team?.toLowerCase(),
        ) as any;
        if (match && Object.keys(match).length > 0) {
          const team: contributingTeamsProp = {
            team: match?.team as string,
            vs: match?.parent as string,
            teamName: match?.teamname,
            vsName: match?.parentname,
          };
          setContributingTeamsList(oldArr => [...oldArr, team]);
        }
      }
    }
  }, [store, response]);

  useEffect(() => {
    handleContributingList();
  }, [handleContributingList]);

  const handleEdit = () => {
    const url = productCatalogBaseUrl.concat(entity.metadata.name);
    window.open(url, '_blank');
  };

  return (
    <InfoCard title="Contributing Teams">
      <button className={classes.button} onClick={handleEdit}>
        <Edit />
      </button>
      {!entity?.spec?.owningteam ||
        (entity?.spec?.owner && contributingTeamsList?.length < 1 && (
          <EmptyState
            missing="data"
            title="No Contributing Team(s) found for this product"
          />
        ))}
      {(entity?.spec?.owningteam || entity?.spec?.owner) &&
        contributingTeamsList?.length > 0 && (
          <Table aria-label="group list" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Team</TableCell>
                <TableCell>Value Stream</TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  Contributing Team in the Lead
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contributingTeamsList.length > 0 ? (
                contributingTeamsList
                  .filter((item, index, self) => {
                    return self.findIndex(x => x.team === item.team) === index;
                  })
                  .sort(x => {
                    const mainContributingTeam =
                      entity?.spec?.mainContributingTeam || '';
                    const resolvedContributingTeamName = list.find(
                      (x: { uid: any }) => x.uid === mainContributingTeam,
                    );
                    const isMainContributingTeam =
                      resolvedContributingTeamName?.name
                        ?.toLowerCase()
                        ?.trim() === x?.teamName?.toLowerCase()?.trim();
                    if (isMainContributingTeam) return -1;

                    return 0;
                  })
                  .map((item: contributingTeamsProp) => {
                    const mainContributingTeam =
                      entity?.spec?.mainContributingTeam || '';
                    const resolvedContributingTeamName = list.find(
                      (x: { uid: any }) => x.uid === mainContributingTeam,
                    );
                    const isMainContributingTeam =
                      resolvedContributingTeamName?.name
                        ?.toLowerCase()
                        ?.trim() === item?.teamName?.toLowerCase()?.trim();
                    const valueStream = item?.vs?.replaceAll(
                      'group:default/',
                      '',
                    );

                    return (
                      <TableRow key={item.team}>
                        <TableCell>
                          <EntityRefLink
                            entityRef={{
                              kind: 'group',
                              name: item.team,
                              namespace: 'default',
                            }}
                          ></EntityRefLink>
                        </TableCell>

                        <TableCell>
                          <b>
                            <EntityRefLink
                              entityRef={{
                                kind: 'group',
                                name: valueStream ?? 'lufthansa_group',
                                namespace: 'default',
                              }}
                            ></EntityRefLink>
                          </b>
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                          {isMainContributingTeam && (
                            <StarIcon color="primary" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <EmptyState
                  missing="data"
                  title="No contributing team found for the product"
                />
              )}
            </TableBody>
          </Table>
        )}
    </InfoCard>
  );
};

export default CoownersTeams;
